export interface LessonBlock {
  type: 'situation' | 'prompt' | 'why' | 'anna' | 'try' | 'example';
  content: string;
  label?: string;
  instruction?: string;
}

export interface AdvancedBlock {
  type: 'situation' | 'prompt' | 'anna';
  content: string;
}

export interface ModuleLesson {
  id: string;
  number: string;
  title: string;
  blocks: LessonBlock[];
}

export interface AdvancedLesson {
  id: string;
  letter: string;
  title: string;
  blocks: AdvancedBlock[];
}

export const MODULE_INTRO = `"Six months ago I sat in a team meeting while everyone around me talked about Copilot like it was the most natural thing in the world. I nodded along and said nothing. That evening I googled it. The results were either written for IT departments or for people half my age who seemed to find all this obvious.

Neither was much help.

So I figured it out the hard way — trial, error, and a lot of quietly muttering at my screen. What's in this module is everything I wish someone had just told me on day one. It's not complicated. I promise."`;

export const ADVANCED_SECTION_INTRO = `"These are the prompts I didn't find in any beginner's guide. I picked them up by watching what the sharper people around me were actually doing — then quietly trying them myself when nobody was looking.

None of them are complicated. They just require you to think slightly differently about what you're asking Copilot to do. Once they click, you won't go back."`;

export const MODULE1_LESSONS: ModuleLesson[] = [
  {
    id: 'lesson-1-1',
    number: '1.1',
    title: 'What Copilot Actually Is',
    blocks: [
      {
        type: 'situation',
        content:
          'Your company has switched on Microsoft Copilot. People keep mentioning it in meetings. Someone from IT sent a breathless email about it three weeks ago that you skimmed and deleted. You have a vague sense you should probably know what it is by now.\n\nYou do not need to feel bad about this. Most people in your position are in exactly the same place, they\'re just better at hiding it.',
      },
      {
        type: 'prompt',
        content:
          'Open Outlook and look for the Copilot icon — it looks like a small sparkle or star shape, usually in the top right of your inbox or inside an email. Click it. That\'s your starting point.',
        label: 'YOUR FIRST STEP',
        instruction: 'This isn\'t a Copilot prompt yet — it\'s just finding where Copilot lives. Once you can see it, you\'re ready for Lesson 2.',
      },
      {
        type: 'why',
        label: 'What It Is',
        content:
          'Copilot is Microsoft\'s AI assistant. It sits inside the tools you already use every day — Outlook, Teams, Word, Excel, PowerPoint — and helps you do things faster.\n\nThink of it less like a robot and more like a very fast, very patient colleague who never gets tired of drafting things, summarising things, or finding things. You tell it what you need. It does a first pass. You check it, adjust it, and send it.\n\nThat\'s it. That\'s the whole idea.\n\nYou\'ll find Copilot in:\n• Outlook — helps with emails. Reading them, writing them, summarising long threads.\n• Teams — sits inside your meetings. Can summarise what was said, pull out actions, catch you up if you joined late.\n• Word — helps you write. Give it a rough brief and it produces a first draft. You shape it from there.\n• Excel — helps you make sense of data without needing to be a spreadsheet expert.\n• PowerPoint — helps you build presentation outlines and improve slide content.\n\nMost of what\'s in this module also works in ChatGPT, Claude, or Gemini — you\'d just copy and paste your email or document in, since they don\'t sit inside Outlook or Word the way Copilot does. If you don\'t have Copilot through work, everything from here still applies.',
      },
      {
        type: 'anna',
        content:
          'Copilot is not magic. It gets things wrong sometimes. It occasionally sounds generic. It cannot read your mind, and it works better when you give it clear instructions.\n\nThe good news is that giving it clear instructions is exactly what this module teaches you. By the end of it you\'ll be using Copilot in ways that will genuinely save you time every single day.',
      },
      {
        type: 'try',
        content:
          'Open Outlook. Look for the Copilot icon — it looks like a small sparkle or star shape, usually in the top right of your inbox or inside an email. If you can see it, you\'re ready for Lesson 2.\n\nIf you can\'t see it, check with your IT team — your company may still be rolling it out.',
      },
    ],
  },
  {
    id: 'lesson-1-2',
    number: '1.2',
    title: 'Copilot in Outlook',
    blocks: [
      {
        type: 'situation',
        content:
          "It's 8.47am. You have 34 unread emails. One of them is a thread that's been going back and forth between six people for three days and contains what you're fairly sure is an important decision, buried somewhere in the middle. You have a meeting at 9.\n\nOld approach: scroll through the whole thing, try to piece it together, probably miss something.\n\nNew approach: ask Copilot.",
      },
      {
        type: 'prompt',
        content:
          'Summarise this email thread and list any decisions that were made and any actions I need to take, including who owns them.',
      },
      {
        type: 'why',
        content:
          'Open the email thread. Click the Copilot icon inside the email. Paste or type the prompt above. Hit enter.\n\nCopilot will read the entire thread — however long it is — and give you a clean summary in seconds. Decisions at the top, actions below, names attached.\n\nDon\'t have Copilot in Outlook? Copy the email thread and paste it into ChatGPT or Claude with the same prompt — you\'ll get the same kind of summary, just one extra copy-paste step.',
      },
      {
        type: 'example',
        content:
          'Summary of email thread: Q3 Budget Review\n\nKey decisions made:\n• Marketing budget to be reduced by 8% from October\n• Final figures to be confirmed by Finance by end of week\n• No further headcount changes this quarter\n\nActions:\n• You: Submit revised budget forecast by Thursday 5pm\n• Sarah Chen: Circulate updated project codes by Wednesday\n• James: Book follow-up meeting for w/c 14th',
      },
      {
        type: 'try',
        content:
          "Find a long email thread in your inbox — ideally one with several people and several replies. Open it, find Copilot, and paste the prompt. See what comes back.\n\nYou don't need to act on it. Just see what it does.",
      },
    ],
  },
  {
    id: 'lesson-1-3',
    number: '1.3',
    title: 'Copilot in Teams',
    blocks: [
      {
        type: 'situation',
        content:
          "You joined this morning's call two minutes late because your previous meeting ran over. By the time you got in, something had clearly just been decided and everyone was nodding. You spent the rest of the meeting trying to work out what you'd missed while also trying to look like you hadn't missed anything.\n\nSound familiar?",
      },
      {
        type: 'prompt',
        content:
          'I joined this meeting a few minutes late. Summarise what was discussed before I joined, what decisions were made, and what actions were agreed — including who is responsible for each one.',
      },
      {
        type: 'why',
        content:
          "After a Teams meeting ends, go to the meeting chat or the recap tab. Copilot will have been listening throughout (your organisation needs to have this enabled — most do). Open Copilot and paste the prompt.\n\nYou can also use this after any meeting, even ones you attended fully, to get a clean set of notes you can forward to the team without spending twenty minutes writing them up yourself.\n\nIf you don't have Copilot in Teams, you can still do this — copy the meeting transcript or chat recap and paste it into ChatGPT or Claude with the same prompt.",
      },
      {
        type: 'example',
        content:
          'Meeting Summary — Product Roadmap Check-in\n\nBefore you joined:\nThe team agreed to push the Phase 2 launch date back by two weeks to allow for additional testing. This was proposed by David and agreed without objection.\n\nDecisions made:\n• Launch date moved to 14th November\n• Customer communications to be updated to reflect new date\n• Testing scope to be confirmed by end of this week\n\nActions:\n• Marcus: Update project plan and circulate by EOD today\n• Priya: Draft revised customer email for review by Thursday\n• You: Confirm testing scope with the QA team by Friday',
      },
      {
        type: 'try',
        content:
          "After your next Teams meeting, open the meeting recap and try the prompt. Even if you attended the whole meeting, use it to generate your follow-up notes. Send them to the team. Watch how impressed people are that you got them out so fast.",
      },
    ],
  },
  {
    id: 'lesson-1-4',
    number: '1.4',
    title: 'Copilot in Word',
    blocks: [
      {
        type: 'situation',
        content:
          "You need to write a progress report. You know what you want to say. You have the information. But you've been staring at a blank document for twenty minutes and you've written the title and deleted it twice.\n\nThe blank page is not a skill problem. It's a starting problem. Copilot solves starting problems.",
      },
      {
        type: 'prompt',
        content:
          'Write a first draft of a progress report on [project name] for a senior leadership audience. Keep it to one page. Cover what has been achieved this month, what is planned for next month, and flag any risks or issues that need attention. Use clear, professional language.',
      },
      {
        type: 'why',
        content:
          "Open a new Word document. Click the Copilot icon (usually in the Home ribbon or as a floating icon on the page). Type or paste the prompt. Copilot will generate a full first draft.\n\nReplace [project name] with whatever you're reporting on. That's the only thing you need to change.\n\nIt won't be perfect. It doesn't know your project the way you do. But it gives you something to work with instead of nothing — and editing is always faster than writing from scratch.\n\nNo Copilot in Word? Paste the same prompt into ChatGPT or Claude, then copy the draft back into your document.",
      },
      {
        type: 'example',
        content:
          "Progress Report — Customer Portal Redesign\nPrepared for: Senior Leadership Team\nPeriod: October 2026\n\nAchievements this month:\nThe design phase has been completed ahead of schedule, with all three customer journey maps signed off by the business on 18th October. Development has begun on the core login and account management screens, with the first build expected for internal review by 7th November.\n\nPlanned for next month:\nDevelopment of the remaining five screens will continue, with a full internal demo targeted for 28th November. User testing recruitment begins week commencing 11th November.\n\nRisks and issues:\nOne risk to note: the third-party payment integration requires a vendor dependency that is currently showing a two-week lead time. If this is not resolved by 12th November it may affect the December go-live date. This is being monitored and an alternative vendor has been identified as a contingency.",
      },
      {
        type: 'try',
        content:
          "Open Word. Use the prompt with a real project you're working on. Read what comes back, adjust the facts to match reality, and tighten the language where you want to. You've just cut your report-writing time in half.",
      },
    ],
  },
  {
    id: 'lesson-1-5',
    number: '1.5',
    title: 'Copilot in Excel',
    blocks: [
      {
        type: 'situation',
        content:
          "Someone has sent you a spreadsheet. It has a lot of numbers in it. There is an expectation that you will have something intelligent to say about it in tomorrow's meeting.\n\nYou are not a data person. You never have been. And yet here you are.",
      },
      {
        type: 'prompt',
        content:
          'Look at this data and tell me in plain English what the three most important things are that I should notice. Then tell me what questions I should be asking about it.',
      },
      {
        type: 'why',
        content:
          'Open the spreadsheet in Excel. Click the Copilot icon in the Home ribbon. Paste the prompt. Copilot will read the data and give you a plain English interpretation — no formulas required, no pivot tables, no expertise needed.\n\nDon\'t have Copilot in Excel? Copy your data (or a summary of it) into ChatGPT or Claude and use the same prompt — works just as well for spotting what matters.',
      },
      {
        type: 'example',
        content:
          "Three things worth noticing in this data:\n\n1. Sales in the North region are down 14% compared to the same period last year. Every other region is flat or growing slightly. This stands out.\n\n2. The highest-performing product line (Premium Care) accounts for 61% of total revenue but only 23% of total units sold. It's carrying the business.\n\n3. Customer acquisition costs have risen steadily each month for six months. They are now 34% higher than in January.\n\nQuestions worth asking:\n• What happened in the North region specifically?\n• Is the Premium Care dependency a risk if that line faces competition?\n• Is the rise in acquisition costs seasonal or a trend?",
      },
      {
        type: 'try',
        content:
          "Find a spreadsheet you've been avoiding. Open it in Excel, find Copilot, and paste the prompt. Use what comes back as your starting point for any conversation about the data.",
      },
    ],
  },
  {
    id: 'lesson-1-6',
    number: '1.6',
    title: 'Your AI Cheat Sheet',
    blocks: [
      {
        type: 'situation',
        content:
          "You've worked through five tools. That's the foundation. Before you move on, there's one thing worth having close to hand.",
      },
      {
        type: 'why',
        content:
          "The prompts in this module are just the beginning. Your full That Clicked AI Cheat Sheet has 30 more — covering everything from PowerPoint presentations to difficult emails to preparing for performance reviews.\n\nIt's waiting for you on your dashboard. Download it, print it if you like, and keep it somewhere handy.",
      },
      {
        type: 'anna',
        content:
          "The goal isn't to memorise prompts. The goal is to have them close enough that reaching for Copilot becomes second nature — the same way you'd reach for spell check or a calculator.",
      },
      {
        type: 'try',
        content:
          'Download your Copilot Cheat Sheet from the button below. Print it if you like, or save it somewhere you can find it quickly. Then come back and start Module 2.',
      },
    ],
  },
];

export const ADVANCED_LESSONS: AdvancedLesson[] = [
  {
    id: 'advanced-a',
    letter: 'A',
    title: 'Give Copilot a Role',
    blocks: [
      {
        type: 'situation',
        content:
          "You've written something — a report, a proposal, an email to a difficult stakeholder — and you want to know if it's actually good before you send it. Your instinct is to ask a colleague. But you know what you'll get: \"Looks fine.\"\n\nCopilot can do better than fine. If you tell it who to be first.",
      },
      {
        type: 'prompt',
        content:
          'Act as a sceptical senior executive who has seen a hundred proposals like this one. Read what I\'ve written and tell me the three things that would make you push back, question the logic, or lose confidence in the author. Be direct.',
      },
      {
        type: 'anna',
        content:
          "I used this before a board presentation last year. Copilot as 'sceptical CFO' found two things I'd glossed over that I genuinely hadn't noticed. I fixed them. The actual CFO said nothing. That felt like a win.\n\nYou can swap the role for anything that fits:\n• \"Act as a finance director focused on cost\"\n• \"Act as someone who knows nothing about this project\"\n• \"Act as the most cynical person in the meeting\"\n\nThis works exactly the same in ChatGPT or Claude — the role-play instruction isn't Copilot-specific at all.",
      },
    ],
  },
  {
    id: 'advanced-b',
    letter: 'B',
    title: 'Chain Your Prompts',
    blocks: [
      {
        type: 'situation',
        content:
          "Most people use Copilot once, take what it gives them, and stop. The people getting the most out of it treat it like a conversation — each prompt building on the last.",
      },
      {
        type: 'prompt',
        content:
          'Prompt 1: Write a first draft executive summary of this document. Keep it to three paragraphs. Senior audience.\n\nPrompt 2 (after reading what comes back): Now make it 30% shorter without losing any of the key points. Cut anything that sounds like filler.\n\nPrompt 3 (to sharpen further): Read this back as if you\'re the intended audience seeing it for the first time. What\'s the one thing that\'s still unclear or could be misread?',
      },
      {
        type: 'anna',
        content:
          "I used to think one prompt should do everything. It shouldn't. Think of it the way you'd think of a conversation with a good editor — you go back and forth until it's right. Copilot is very patient.\n\nThe first prompt gets you a draft. The second makes it tighter. The third finds the hole you missed. Three prompts, sixty seconds, something genuinely good.\n\nThis approach works in ChatGPT and Claude too — same logic, same results.",
      },
    ],
  },
  {
    id: 'advanced-c',
    letter: 'C',
    title: 'Argue Against Yourself',
    blocks: [
      {
        type: 'situation',
        content:
          "You've made a recommendation. You're fairly confident in it. But you know someone in the meeting will push back — you just don't know exactly how or where.\n\nThis prompt finds the weak spots before they do.",
      },
      {
        type: 'prompt',
        content:
          "Here is a recommendation I'm about to make to my leadership team: [paste your recommendation]\n\nI need you to argue against it. Give me the five strongest counterarguments someone could make — the ones that would actually land in a room full of experienced people. Don't soften them.",
      },
      {
        type: 'anna',
        content:
          "This one felt uncomfortable the first time I used it. Copilot was quite good at finding the holes. That's the point. Better to find them at your desk than in front of twelve people.\n\nYou either discover your recommendation is stronger than you thought — because the counterarguments don't hold up. Or you find the real weaknesses and fix them first. Either way you walk into the room better prepared.\n\nWorks identically in ChatGPT or Claude — this is about the prompt technique, not the tool.",
      },
    ],
  },
  {
    id: 'advanced-d',
    letter: 'D',
    title: 'Prep for Difficult People',
    blocks: [
      {
        type: 'situation',
        content:
          "You know who's going to be in the room. You also know who's going to be the problem. They always challenge on the same things — budget, timeline, risk, whatever their particular obsession is. You can prepare for them specifically.",
      },
      {
        type: 'prompt',
        content:
          "I'm presenting this proposal to a group that includes someone who always challenges on cost and will push back on anything that doesn't have a clear ROI. Based on what I've written, what questions are they most likely to ask — and what are the strongest answers I could give?\n\nAdapt the description to fit whoever you're dealing with:\n• \"Someone who always worries about risk and compliance\"\n• \"Someone who will question whether we have the resource to deliver this\"\n• \"Someone who supported a competing proposal last year\"",
      },
      {
        type: 'anna',
        content:
          "I have used this more than any other prompt on this list. There's one person in my organisation who can derail any meeting if you're not ready for them. I'm always ready for them now.\n\nYou're not just preparing answers. You're thinking through the room dynamics before you're in them. And you're doing it in five minutes instead of half a day.\n\nSame prompt, same result in ChatGPT or Claude — nothing here is Copilot-specific.",
      },
    ],
  },
  {
    id: 'advanced-e',
    letter: 'E',
    title: 'The Before/After Prompt',
    blocks: [
      {
        type: 'situation',
        content:
          "You've written something yourself — in your own voice, with your own judgment — but you know it could be sharper. You don't want Copilot to rewrite it from scratch and lose what makes it yours. You just want it better.",
      },
      {
        type: 'prompt',
        content:
          "Here is something I've written: [paste your text]\n\nDon't rewrite it. Don't change my voice or my structure. Just identify the three sentences or phrases that are weakest — where the writing goes vague, loses confidence, or could be misread — and suggest a tighter version of each one only.",
      },
      {
        type: 'anna',
        content:
          "This is how I use Copilot for anything important. I write it. Copilot tightens it. Nobody would know the difference — because the thinking and the voice are still mine. Which is how it should be.\n\nThis keeps you in control. Copilot improves specific weak spots rather than producing something that sounds like Copilot wrote it. The result reads like you — just a slightly sharper version of you.\n\nThis works the same way in ChatGPT or Claude — tightening your own writing isn't tool-specific.",
      },
    ],
  },
];

// Lessons 1.1 and 1.2 are always pre-completed
export const PRE_COMPLETED_LESSONS = ['lesson-1-1', 'lesson-1-2'];
